import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface Tier {
  step: string;
  title: string;
  what: string;
  bestFor: string;
  flag?: string;
}

const tiers: Tier[] = [
  {
    step: "01",
    title: "AI Assessment",
    what: "A focused audit of your business to find where AI and automation actually move the needle — with a clear, prioritized roadmap you own.",
    bestFor: "Best for: anyone not sure where to start. This is the front door.",
    flag: "Start here",
  },
  {
    step: "02",
    title: "AI Concierge",
    what: "Ongoing, hands-on support — I stay in your corner month to month, implementing, tuning, and advising as your needs evolve.",
    bestFor: "Best for: owners who want a partner, not a one-off project.",
  },
  {
    step: "03",
    title: "Process Redesign",
    what: "We map a slow, manual workflow end to end and rebuild it around AI — fewer steps, fewer errors, hours back every week.",
    bestFor: "Best for: a specific process that's eating your team's time.",
  },
  {
    step: "04",
    title: "Automation Build",
    what: "I build the reminders, follow-ups, scheduling, and hand-offs that run themselves — synced to the tools you already use.",
    bestFor: "Best for: repetitive admin and scheduling that should be hands-free.",
  },
  {
    step: "05",
    title: "Knowledge System",
    what: "Your policies, pricing, and documents become a searchable, AI-ready source of truth — so answers and drafts stay consistent.",
    bestFor: "Best for: teams drowning in documents, intake notes, and FAQs.",
  },
  {
    step: "06",
    title: "Custom Workflows",
    what: "When your process is one of a kind, I build to fit — integrations, internal tools, and specialty automations that fit your stack.",
    bestFor: "Best for: unique workflows that off-the-shelf tools can't handle.",
  },
  {
    step: "07",
    title: "Full Implementation",
    what: "The complete engagement — assessment, redesign, builds, and a knowledge system delivered together as one coordinated rollout.",
    bestFor: "Best for: businesses ready to go all-in and transform how they run.",
    flag: "Most comprehensive",
  },
];

const EngagementLadder = () => {
  return (
    <section id="how-we-work" className="py-16 md:py-24 bg-secondary">
      <div className="container mx-auto max-w-5xl">
        <header className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold font-display text-secondary-foreground">
            How We Work Together
          </h2>
          <p className="text-lg md:text-xl text-cream-muted mt-3 max-w-2xl mx-auto">
            Seven ways to bring AI into your business — from a single assessment to a full rollout.
            Not sure which fits? Start with the assessment and I'll point you to the right one.
          </p>
        </header>

        <motion.ol
          className="relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
        >
          {/* Connecting spine */}
          <span
            aria-hidden
            className="absolute left-[1.35rem] md:left-[1.6rem] top-2 bottom-2 w-px bg-cream-muted/25"
          />

          {tiers.map(({ step, title, what, bestFor, flag }) => (
            <motion.li
              key={step}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.4, ease: "easeOut" as const },
                },
              }}
              className="relative flex gap-5 md:gap-7 pb-8 last:pb-0"
            >
              {/* Step number node */}
              <span className="relative z-10 shrink-0 inline-flex size-11 md:size-[3.25rem] items-center justify-center rounded-full bg-primary text-primary-foreground font-display text-lg md:text-xl font-semibold shadow-md">
                {step}
              </span>

              <div className="flex-1 pt-1 md:pt-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl md:text-2xl font-semibold text-secondary-foreground">
                    {title}
                  </h3>
                  {flag && (
                    <span className="inline-flex items-center rounded-full bg-primary/15 px-3 py-0.5 text-xs font-medium uppercase tracking-wide text-primary-foreground/90 ring-1 ring-primary/40">
                      {flag}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-base md:text-lg text-secondary-foreground/80 leading-relaxed max-w-2xl">
                  {what}
                </p>
                <p className="mt-1.5 text-sm md:text-base text-cream-muted">{bestFor}</p>
              </div>
            </motion.li>
          ))}
        </motion.ol>

        <div className="mt-14 text-center">
          <p className="text-base md:text-lg text-cream-muted mb-4">
            Every engagement starts with a free audit — no cost, no pressure.
          </p>
          <Button asChild size="lg">
            <a href="#services" aria-label="Book your free audit">
              Book Your Free Audit
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EngagementLadder;
